import React, { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "primereact/button";
import PreLoan from "./PreLoan";
import InsuranceStepModal from "./PreLoanSteps/insurance/InsuranceStepModal";
import PlatesStepModal from "./PreLoanSteps/plates/PlatesStepModal";
import useHeaderStepsStatus from "../../../../Hooks/useHeaderStepsStatus";

const HEADER_STEPS = [
  { label: "Contrato", icon: "pi pi-file", key: "contrato" },
  { label: "Seguros", icon: "pi pi-shield", key: "seguros" },
  { label: "Placas", icon: "pi pi-car", key: "placas" },
  { label: "GPS", icon: "pi pi-map-marker", key: "gps" },
];

// Icono auxiliar que se superpone al icono base según el estado del paso.
// Solo se renderiza para los estados de alerta (error/warning); los demás
// estados quedan con el icono original sin overlay.
const STATE_OVERLAY_ICON = {
  error: { icon: "pi pi-exclamation-triangle", color: "#dc2626" },
  warning: { icon: "pi pi-clock", color: "#f59e0b" },
};

/** Get initials from a name string, e.g. "Juan Carlos" → "JC" */
function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("");
}

export default function DashboardHeader({
  onBack,
  onBackToDashboard,
  persona,
  people,
  onPersonChange,
  canContinue,
  setCategories,
  setVisible,
  visible,
  toast,
  categoriasFaltantes,
}) {
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [contractFinalized, setContractFinalized] = useState(false); // legacy local (fallback)
  const [showInsuranceModal, setShowInsuranceModal] = useState(false);
  const [showPlatesModal, setShowPlatesModal] = useState(false);
  const switcherRef = useRef(null);
  const searchInputRef = useRef(null);

  // Status real de cada paso (Contrato/Seguros/Placas/GPS) leído del backend.
  // Se refresca cuando cambia el cliente y manualmente vía `refreshSteps`.
  const { steps: stepsStatus, refresh: refreshSteps } = useHeaderStepsStatus(persona?.id);

  // console.log("[Seguros DH] render", {
  //   canContinue,
  //   canContinue_truthy: !!canContinue,
  //   personaId: persona?.id,
  //   hasFinancialDetails: persona?.infoBudget?.financial_details != null,
  //   categoriasFaltantesCount: categoriasFaltantes?.length ?? 0,
  //   showInsuranceModal,
  // });

  // Close on outside click
  useEffect(() => {
    if (!switcherOpen) return;
    const handler = (e) => {
      if (switcherRef.current && !switcherRef.current.contains(e.target)) {
        setSwitcherOpen(false);
        setSearchQuery("");
        setTypeFilter("all");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [switcherOpen]);

  // Focus search on open
  useEffect(() => {
    if (switcherOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 120);
    }
  }, [switcherOpen]);

  // Close on Escape
  useEffect(() => {
    if (!switcherOpen) return;
    const handler = (e) => {
      if (e.key === "Escape") {
        setSwitcherOpen(false);
        setSearchQuery("");
        setTypeFilter("all");
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [switcherOpen]);

  const filteredPeople = (people || []).filter((p) => {
    const matchesName = p.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === "all" || p.persona_subtype === typeFilter;
    return matchesName && matchesType;
  });

  const handleSelectUser = useCallback(
    (user) => {
      setVisible(false);
      setSwitcherOpen(false);
      setSearchQuery("");
      setTypeFilter("all");
      onPersonChange(user);
    },
    [onPersonChange, setVisible]
  );

  return (
    <>
      {onBackToDashboard && (
        <Button
          label="Regresar al dashboard"
          icon="pi pi-arrow-left"
          className="p-button-text p-0 mb-2"
          style={{ color: '#1A9AC4', textDecoration: 'underline' }}
          onClick={onBackToDashboard}
        />
      )}
      <motion.div
        className="dh-card"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 280, damping: 24 }}
      >
        {/* ── Zona Izquierda: Título ── */}
        <div className="dh-title-section">
          {onBack && (
            <Button
              icon="pi pi-arrow-left"
              className="p-button-text p-button-rounded dh-back-btn"
              onClick={onBack}
              tooltip="Volver al Dashboard"
              tooltipOptions={{ position: "bottom" }}
            />
          )}
          <div className="dh-icon-box">
            <i className="pi pi-folder" />
          </div>
          <div className="dh-title-text">
            <h1 className="dh-title">
              Documentoxxxs <span className="dh-title-sep">—</span> {persona?.name_syntage || persona?.name}
            </h1>
            <p className="dh-subtitle">Gestión #{persona?.id || "..."}</p>
          </div>
        </div>

        {/* ── Zona Centro: Steps Navigation ── */}
        <nav className="dh-steps-nav py-2" aria-label="Progress">
          <ol className="dh-steps-list">
            {HEADER_STEPS.map((step, idx) => {
              const isFirst = idx === 0;
              const isLast = idx === HEADER_STEPS.length - 1;

              // Status real del backend (con fallback a flag local viejo solo
              // para Contrato si aún no hay step backend).
              const backendStatus = stepsStatus?.[step.key];
              let stepState = backendStatus?.status || "pending";
              if (step.key === "contrato" && stepState === "pending" && contractFinalized) {
                stepState = "completed";
              }
              const stateLabel = backendStatus?.label || "Pendiente";
              const overlay = STATE_OVERLAY_ICON[stepState] || null;

              return (
                <motion.li
                  key={step.key}
                  className="dh-step-item"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + idx * 0.07, type: "spring", stiffness: 350, damping: 22 }}
                >
                  <div
                    // onClick={() => {
                    //   console.log("[Seguros DH] wrapper-click", { key: step.key, idx, canContinue });
                    // }}
                    style={{ display: "inline-block" }}
                  >
                    <button
                      className={`dh-step-circle ${stepState}`}
                      onMouseDown={() => console.log("[Seguros DH] mousedown", { key: step.key, idx, canContinue })}
                      onClick={(ev) => {
                        // console.log("[Seguros DH] click step", {
                        //   key: step.key,
                        //   idx,
                        //   canContinue,
                        //   eventTarget: ev?.target?.tagName,
                        //   personaId: persona?.id,
                        // });
                        if (step.key === "contrato") {
                          if (!canContinue) {
                            // console.warn("[Seguros DH] contrato BLOCKED: canContinue=false");
                            return;
                          }
                          // console.log("[Seguros DH] opening contrato dialog");
                          setVisible(true);
                        } else if (step.key === "seguros") {
                          //console.log("[Seguros DH] opening insurance modal (canContinue gate disabled for debug)");
                          setShowInsuranceModal(true);
                        } else if (step.key === "placas") {
                          // Placas SIEMPRE abre el modal. El propio PlatesStepModal
                          // muestra un estado bloqueado visible ("Completa el paso de
                          // Seguros antes de tramitar las placas") vía insurance_completed
                          // y deshabilita las acciones; el backend además rechaza
                          // email/complete con 409 si Seguros no está completo. Así el
                          // gating es visible y no un return silencioso.
                          console.log("[Placas DH] opening plates modal");
                          setShowPlatesModal(true);
                        } else {
                          //console.log("[Seguros DH] click on", step.key, "(no handler)");
                        }
                      }}
                      title={!canContinue
                        ? (persona?.infoBudget?.financial_details == null
                          ? "La cotización requiere detalles financieros"
                          : categoriasFaltantes?.length > 0
                            ? `Faltan: ${categoriasFaltantes.join(", ")}`
                            : "")
                        : `${step.label} · ${stateLabel}`}
                    >
                      {stepState === "completed" ? (
                        <span className="dh-step-num">{idx + 1}</span>
                      ) : (
                        <i className={step.icon} />
                      )}
                      {overlay && (
                        <i
                          className={`${overlay.icon} dh-step-overlay`}
                          style={{ color: overlay.color }}
                          aria-hidden="true"
                        />
                      )}
                    </button>
                  </div>
                  <span className={`dh-step-label ${stepState}`}>{step.label}</span>

                  {/* Chevron separator */}
                  {!isLast && (
                    <svg className="dh-step-chevron-svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
                    </svg>
                  )}
                </motion.li>
              );
            })}
          </ol>
        </nav>

        {/* ── Zona Derecha: User Switcher ── */}
        <div className="dh-user-section" ref={switcherRef}>
          <span className="dh-user-label">Usuario actussssal</span>
          <button
            className="dh-user-trigger"
            onClick={() => setSwitcherOpen((prev) => !prev)}
            aria-expanded={switcherOpen}
          >
            <div className="dh-avatar">
              {getInitials(persona?.name_syntage || persona?.name)}
            </div>
            <div className="dh-user-trigger-text">
              <span className="dh-user-trigger-name">{persona?.name_syntage || persona?.name || "..."}</span>
              <span className="dh-user-trigger-type">
                ({persona?.persona_subtype || (persona?.tipo_persona === "PF" ? "PF" : "PM")})
              </span>
            </div>
            <i className={`pi pi-chevron-down dh-user-chevron ${switcherOpen ? "open" : ""}`} />
          </button>

          {/* ── Switcher Panel ── */}
          <AnimatePresence>
            {switcherOpen && (
              <>
                {/* Backdrop */}
                <motion.div
                  className="dh-switcher-backdrop"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  onClick={() => { setSwitcherOpen(false); setSearchQuery(""); setTypeFilter("all"); }}
                />

                {/* Panel */}
                <motion.div
                  className="dh-switcher-panel"
                  initial={{ opacity: 0, y: -8, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.97 }}
                  transition={{ type: "spring", stiffness: 400, damping: 28 }}
                >
                  {/* Header */}
                  <div className="dh-switcher-header">
                    <h3 className="dh-switcher-title">Cambiar usuario</h3>
                  </div>

                  {/* Search + Filters */}
                  <div className="dh-switcher-search-wrap">
                    <div className="dh-switcher-toolbar">
                      <div className="dh-switcher-search-box">
                        <i className="pi pi-search dh-switcher-search-icon" />
                        <input
                          ref={searchInputRef}
                          className="dh-switcher-search-input"
                          type="text"
                          placeholder="Buscar usuario..."
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        {searchQuery && (
                          <button
                            className="dh-switcher-search-clear"
                            onClick={() => setSearchQuery("")}
                          >
                            <i className="pi pi-times" />
                          </button>
                        )}
                      </div>
                      <div className="dh-switcher-filters">
                        {[{ key: "PFAE", label: "PFAE" }, { key: "PF", label: "PF" }, { key: "PM", label: "PM" }].map((f) => (
                          <button
                            key={f.key}
                            className={`dh-switcher-filter-btn ${typeFilter === f.key ? "active" : ""}`}
                            onClick={() => setTypeFilter(typeFilter === f.key ? "all" : f.key)}
                          >
                            {f.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* User list */}
                  <div className="dh-switcher-list">
                    {/* Active user */}
                    {persona && (
                      <>
                        <div className="dh-switcher-item active">
                          <div className="dh-avatar sm">
                            {getInitials(persona.name)}
                          </div>
                          <div className="dh-switcher-item-text">
                            <div className="dh-switcher-item-row">
                              <span className="dh-switcher-item-name">{persona.name}</span>
                              <span className="dh-switcher-active-badge">Activo</span>
                            </div>
                            <span className="dh-switcher-item-sub">
                              {persona.persona_subtype === "PFAE" ? "Física AE" : persona.persona_subtype === "PF" ? "Persona Física" : persona.persona_subtype === "PM" ? "Persona Moral" : persona.tipo_persona === "PF" ? "Persona Física" : "Persona Moral"}
                              {persona.fecha_generacion && ` · ${new Date(persona.fecha_generacion).toLocaleDateString("es-MX")}`}
                            </span>
                          </div>
                        </div>

                        <div className="dh-switcher-divider" />
                        <p className="dh-switcher-section-label">Cuentas disponibles</p>
                      </>
                    )}

                    {/* Other users */}
                    {filteredPeople
                      .filter((p) => p.id !== persona?.id)
                      .map((user) => (
                        <motion.button
                          key={user.id}
                          className="dh-switcher-item clickable"
                          onClick={() => handleSelectUser(user)}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.12 }}
                        >
                          <div className="dh-avatar sm">
                            {getInitials(user.name)}
                          </div>
                          <div className="dh-switcher-item-text">
                            <div className="dh-switcher-item-row">
                              <span className="dh-switcher-item-name">{user.name}</span>
                              <span className="dh-switcher-switch-label">Cambiar</span>
                            </div>
                            <span className="dh-switcher-item-sub">
                              {user.persona_subtype === "PFAE" ? "Física AE" : user.persona_subtype === "PF" ? "Persona Física" : user.persona_subtype === "PM" ? "Persona Moral" : user.tipo_persona === "PF" ? "Persona Física" : "Persona Moral"}
                              {user.fecha_generacion && ` · ${new Date(user.fecha_generacion).toLocaleDateString("es-MX")}`}
                            </span>
                          </div>
                        </motion.button>
                      ))}

                    {filteredPeople.filter((p) => p.id !== persona?.id).length === 0 && (
                      <div className="dh-switcher-empty">
                        <i className="pi pi-search" />
                        <span>No se encontraron usuarios</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* ── PreLoan Dialog ── */}
        <PreLoan
          persona={persona}
          canContinue={canContinue}
          categoriasFaltantes={categoriasFaltantes}
          visible={visible}
          setVisible={setVisible}
          toast={toast}
          onContractFinalized={setContractFinalized}
        />

        {/* ── Insurance Step Modal ── */}
        {/* {(() => { console.log("[Seguros DH] render: showInsuranceModal=", showInsuranceModal, "personaId=", persona?.id); return null; })()} */}
        {showInsuranceModal && (
          <InsuranceStepModal
            visible={showInsuranceModal}
            onHide={() => {
              setShowInsuranceModal(false);
              // Re-sincroniza el chip Seguros del header al cerrar el modal
              // (puede haber cambiado: completed, cancelled, etc.)
              refreshSteps();
            }}
            onChanged={refreshSteps}
            personaId={persona?.id}
            persona={persona}
            cotizadorPrice={persona?.infoBudget?.insurance?.amount ?? null}
            toast={toast}
          />
        )}

        {/* ── Plates Step Modal ── */}
        {showPlatesModal && (
          <PlatesStepModal
            visible={showPlatesModal}
            onHide={() => {
              setShowPlatesModal(false);
              // Re-sincroniza el chip Placas del header al cerrar el modal
              // (puede haber cambiado: completed, cancelled, etc.)
              refreshSteps();
            }}
            onChanged={refreshSteps}
            personaId={persona?.id}
            persona={persona}
            toast={toast}
          />
        )}
      </motion.div>
    </>
  );
}
